import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useThemeContext } from 'contexts';
import { savedSearchRepository } from 'data/saved-searches/repository';
import generateSearchDescriptionFromFilters from 'utils/generate-search-description-from-filters';
import { SavedSearchDraft } from 'domain/saved-search';
import type { AlertFrequency, MapBounds, SavedSearchRecord } from 'domain/saved-search';
import type { Filter } from 'contexts/preferences/listing-params/types';

/** Default number of saved searches listed — what the area page's panel shows. */
const RECENTS_LIMIT = 3;
const RECENTS_QUERY_KEY = 'saved-search-recents';

// TODO: rename this hook to `useSavedSearches` once the legacy `use-saved-searches.ts`
// (the 5-minute-cache hook) is retired and the name frees up. It's a React Query
// data hook now — not a store — so the name should describe what it returns.

export interface SaveCurrentSearchOptions {
  name?: string;
  frequency?: AlertFrequency;
  /**
   * The map's current rectangle. Recorded on the saved search as `bbox` when the
   * search is located by the map — it has no area slug, or `locatedByMap` says
   * so. Callers on an area page can leave it out, since the slug locates the
   * search and client-app would ignore a box beside one.
   */
  bounds?: MapBounds | null;
  /**
   * True on a page where the map is the search (the search page). Such a search
   * is saved without a slug, so a slug left in the filter by an earlier area
   * page or a location search cannot decide it. See `SavedSearchDraftInput`.
   */
  locatedByMap?: boolean;
}

export interface EditSavedSearchChanges {
  name?: string;
  frequency?: AlertFrequency;
}

export interface UseSavedSearchStoreOptions {
  /**
   * How many of the most recent saved searches to list. Defaults to
   * {@link RECENTS_LIMIT}; callers that show a full list (e.g. the Collections
   * pane's Saved Searches tab) pass a larger one. Each limit gets its own cache
   * entry, so a short list and a full list can coexist without evicting each other.
   */
  limit?: number;
}

export interface UseSavedSearchStoreResult {
  /** The most recent saved searches as domain records, up to the requested limit. */
  recents: SavedSearchRecord[];
  isLoading: boolean;
  error: unknown;
  fetchRecents: () => Promise<void>;
  prefetchRecents: () => void;
  /** Persists the current search params as a new named saved search; resolves to the created record (or undefined if it was not saved). */
  saveCurrentSearch: (filter: Filter, options?: SaveCurrentSearchOptions) => Promise<SavedSearchRecord | undefined>;
  /** Convenience edit for the common area-page case: change alert frequency. */
  updateFrequency: (record: SavedSearchRecord, frequency: AlertFrequency) => Promise<void>;
  /** General edit: rename and/or change frequency. */
  editSavedSearch: (record: SavedSearchRecord, changes: EditSavedSearchChanges) => Promise<void>;
  removeSavedSearch: (record: SavedSearchRecord) => Promise<void>;
}

/**
 * Reads and mutates the current tenant's recent saved searches via the
 * `savedSearchRepository`, backed by React Query. The recents list lives in the
 * query cache (not local `useState`); edits and deletes update that cache
 * optimistically and roll back on failure. Only domain objects cross this
 * boundary — the repository owns all DTO mapping.
 *
 * The query is lazy: the caller triggers the first load via `fetchRecents` (the
 * panel does so when it opens), so we don't fetch until the UI is shown.
 */
export function useSavedSearchStore({ limit = RECENTS_LIMIT }: UseSavedSearchStoreOptions = {}): UseSavedSearchStoreResult {
  const { themeName: source } = useThemeContext();
  const queryClient = useQueryClient();
  const queryKey = [RECENTS_QUERY_KEY, source, limit];
  const [error, setError] = useState<unknown>(undefined);

  const { data, isFetching, refetch, error: queryError } = useQuery({
    queryKey,
    queryFn: () => savedSearchRepository.listRecents(limit, source),
    enabled: false,
  });
  const recents = data ?? [];

  const fetchRecents = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const prefetchRecents = useCallback(() => {
    void refetch();
  }, [refetch]);

  const { mutateAsync: runCreate } = useMutation({
    mutationFn: (draft: SavedSearchDraft) => savedSearchRepository.create(draft, source),
    onSuccess: () => {
      void refetch(); // can't optimistically insert without the server-assigned id
    },
    onError: setError,
  });

  const { mutateAsync: runUpdate } = useMutation({
    mutationFn: (next: SavedSearchRecord) => savedSearchRepository.update(next, source),
    onMutate: async (next: SavedSearchRecord) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SavedSearchRecord[]>(queryKey);
      queryClient.setQueryData<SavedSearchRecord[]>(queryKey, current =>
        (current ?? []).map(record => (record.id === next.id ? next : record))
      );
      return { previous };
    },
    onError: (caught, _next, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous); // reconcile: roll back
      }
      setError(caught);
    },
  });

  const { mutateAsync: runRemove } = useMutation({
    mutationFn: (record: SavedSearchRecord) => savedSearchRepository.remove(record.id),
    onMutate: async (record: SavedSearchRecord) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SavedSearchRecord[]>(queryKey);
      queryClient.setQueryData<SavedSearchRecord[]>(queryKey, current =>
        (current ?? []).filter(existing => existing.id !== record.id)
      );
      return { previous };
    },
    onSuccess: () => {
      void refetch(); // reload the list so a previously-cut-off search fills the gap
    },
    onError: (caught, _record, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous); // reconcile: roll back
      }
      setError(caught);
    },
  });

  const saveCurrentSearch = useCallback(async (filter: Filter, options: SaveCurrentSearchOptions = {}) => {
    setError(undefined);
    const draft = SavedSearchDraft.create({
      filter,
      source,
      name: options.name,
      frequency: options.frequency,
      bounds: options.bounds,
      locatedByMap: options.locatedByMap,
      generateDescription: generateSearchDescriptionFromFilters,
    });
    const validation = draft.validate();
    if (!validation.ok) {
      setError(validation.errors);
      return undefined;
    }
    try {
      return await runCreate(draft);
    } catch {
      // surfaced via the mutation's onError
      return undefined;
    }
  }, [runCreate, source]);

  const updateFrequency = useCallback(async (record: SavedSearchRecord, frequency: AlertFrequency) => {
    setError(undefined);
    try {
      await runUpdate(record.withFrequency(frequency));
    } catch (caught) {
      // The mutation's onError reports anything thrown inside mutationFn, but a
      // throw from withFrequency or from the data layer before its fetch never
      // reaches it. Swallowing those left the control silently doing nothing.
      setError(caught);
    }
  }, [runUpdate]);

  const editSavedSearch = useCallback(async (record: SavedSearchRecord, changes: EditSavedSearchChanges) => {
    setError(undefined);
    let next = record;
    if (changes.name !== undefined) {
      next = next.withDescription(changes.name);
    }
    if (changes.frequency !== undefined) {
      next = next.withFrequency(changes.frequency);
    }
    try {
      await runUpdate(next);
    } catch {
      // surfaced via the mutation's onError
    }
  }, [runUpdate]);

  const removeSavedSearch = useCallback(async (record: SavedSearchRecord) => {
    setError(undefined);
    try {
      await runRemove(record);
    } catch {
      // surfaced via the mutation's onError
    }
  }, [runRemove]);

  return {
    recents,
    isLoading: isFetching,
    error: error ?? queryError ?? undefined,
    fetchRecents,
    prefetchRecents,
    saveCurrentSearch,
    updateFrequency,
    editSavedSearch,
    removeSavedSearch,
  };
}
